import type { Client, DropinPackage } from '../../types/GrooverTypes'
import {
  FreeDropinRuleType,
  FreeDropinRuleValidationErrorCode,
  type BulkFreeDropinRule,
  type FreeDropinGrant,
  type FreeDropinGrantedRecord,
  type FreeDropinRuleValidationError,
  type FreeDropinTicketRule,
  type QualifyingRegistration,
  type SingleActivityFreeDropinRule
} from '../../types/FreeDropinTicketTypes'

/**
 * The legacy persisted shape of a single-activity rule, from before `activityIds` existed: a
 * singular `activityId` and no `activityIds` array. Kept only so `normalizeFreeDropinRule` can
 * accept raw Firestore data that may still be in this shape.
 */
type LegacySingleActivityFreeDropinRule = Omit<SingleActivityFreeDropinRule, 'activityIds'> & {
  activityId: string
}

/** True when the activity belongs to this rule's qualifying set. */
export function ruleCoversActivity(rule: FreeDropinTicketRule, activityId: string): boolean {
  return rule.activityIds.includes(activityId)
}

/**
 * Legacy persisted single-activity rules may still carry the old singular `activityId` field
 * instead of `activityIds`. Normalize any rule read from storage into the current shape:
 * - legacy `{ ruleType: 'single_activity', activityId }` (no `activityIds`) -> `{ ...rule,
 *   activityIds: [activityId] }`, with the legacy field stripped.
 * - already-new-shape single rules and all bulk rules pass through unchanged.
 *
 * Accepts a loosely-typed input (`FreeDropinTicketRule | LegacySingleActivityFreeDropinRule`) since
 * the persisted legacy shape no longer matches `FreeDropinTicketRule` — callers reading raw
 * Firestore data can normalize before use.
 */
export function normalizeFreeDropinRule(
  raw: FreeDropinTicketRule | LegacySingleActivityFreeDropinRule
): FreeDropinTicketRule {
  if (raw.ruleType === FreeDropinRuleType.SingleActivity && !('activityIds' in raw) && 'activityId' in raw) {
    const { activityId, ...rest } = raw
    return { ...rest, activityIds: [activityId] }
  }
  return raw as FreeDropinTicketRule
}

/** Whether a rule is active (undefined/true = active, false = disabled). */
export function isRuleActive(rule: FreeDropinTicketRule): boolean {
  return rule.isActive !== false
}

/** True when this enrollment user is currently an enabled help dancer for the client. */
export function isEnabledHelpDancer(
  client: Pick<Client, 'helpDancerByEnrollmentUserId'>,
  enrollmentUserId: string
): boolean {
  return client.helpDancerByEnrollmentUserId?.[enrollmentUserId]?.enabled === true
}

/**
 * Help dancers do not earn free drop-in rewards unless the rule opts in
 * (`includeHelpDancers === true`). Missing/undefined/false all mean excluded.
 */
export function ruleAllowsHelpDancers(rule: Pick<FreeDropinTicketRule, 'includeHelpDancers'>): boolean {
  return rule.includeHelpDancers === true
}

/** False when the participant is an enabled help dancer and this rule does not include them. */
export function participantQualifiesForFreeDropinRule(
  rule: Pick<FreeDropinTicketRule, 'includeHelpDancers'>,
  client: Pick<Client, 'helpDancerByEnrollmentUserId'>,
  enrollmentUserId: string
): boolean {
  return ruleAllowsHelpDancers(rule) || !isEnabledHelpDancer(client, enrollmentUserId)
}

/**
 * First rule whose qualifying set contains the activity. Since a valid config keeps activity sets
 * disjoint (see `validateFreeDropinRules`), there is at most one match in practice.
 */
export function getRuleForActivity(
  rules: FreeDropinTicketRule[],
  activityId: string
): FreeDropinTicketRule | undefined {
  return rules.find((rule) => ruleCoversActivity(rule, activityId))
}

/**
 * "What counts" for a rule given the qualifying activity IDs registered in the period — the single
 * source of truth shared by `calculateFreeDropinGrants` below and the backend's transactional
 * reconciliation/preview aggregation, so all three agree on dedup behavior instead of drifting.
 *
 * - single_activity: distinct qualifying activities (re-registering the same one doesn't recount).
 * - bulk: raw count, duplicates preserved (unchanged from before).
 */
export function qualifyingCountForRule(rule: FreeDropinTicketRule, qualifyingActivityIds: string[]): number {
  return rule.ruleType === FreeDropinRuleType.SingleActivity
    ? new Set(qualifyingActivityIds).size
    : qualifyingActivityIds.length
}

/**
 * Total ticket entitlement for a rule given its qualifying count (see `qualifyingCountForRule`).
 *
 * - single_activity: `qualifyingCount * ticketsGranted` — each distinct qualifying activity grants
 *   `ticketsGranted` independently. Backward compatible: a legacy rule with exactly one activity in
 *   its set can only ever have `qualifyingCount` 0 or 1, so this is still 0 or `ticketsGranted`.
 * - bulk: the highest tier whose `qualifyingCount` is <= count. Below the lowest tier → 0;
 *   at or above the highest tier → the highest tier's amount (order-independent, uses max).
 */
export function entitledTicketsForRule(rule: FreeDropinTicketRule, qualifyingCount: number): number {
  if (rule.ruleType === FreeDropinRuleType.SingleActivity) {
    return qualifyingCount * rule.ticketsGranted
  }
  return rule.thresholds.reduce(
    (best, tier) => (qualifyingCount >= tier.qualifyingCount ? Math.max(best, tier.ticketsGranted) : best),
    0
  )
}

const groupKey = (ruleId: string, periodKey: string): string => `${ruleId}::${periodKey}`

type Group = { ruleId: string; periodKey: string; activityIds: string[] }

export type CalculateFreeDropinGrantsInput = {
  rules: FreeDropinTicketRule[]
  registrations: QualifyingRegistration[]
  /** Prior granted totals per (rule, period), used to compute the signed delta. */
  alreadyGranted?: FreeDropinGrantedRecord[]
}

/**
 * Recompute drop-in ticket entitlement per (rule, period) from the current qualifying registrations
 * and emit the signed delta versus what was already granted.
 *
 * The full-recompute + delta model makes repeated runs idempotent (unchanged input → delta 0) and
 * lets cancellation/refund (registrations dropping out) shrink the entitlement to a negative delta
 * (revoke) rather than needing per-registration bookkeeping.
 *
 * Only active rules are considered. Inactive/removed rules are ignored entirely, so deactivating a
 * rule never revokes tickets already granted under it (rule lifecycle: future purchases only).
 * Counting mirrors the discount engine's `userEnrolledPeriodIds` primitive — one qualifying
 * registration per entry, duplicates preserved; `qualifyingCountForRule` then dedupes per rule type
 * (distinct activities for single-activity rules, raw count for bulk) before `entitledTicketsForRule`
 * computes the entitlement.
 */
export function calculateFreeDropinGrants(input: CalculateFreeDropinGrantsInput): FreeDropinGrant[] {
  const activeRules = input.rules.filter(isRuleActive)
  const activeRuleById = new Map(activeRules.map((rule) => [rule.id, rule]))

  const alreadyGrantedByKey = new Map(
    (input.alreadyGranted ?? []).map((record) => [groupKey(record.ruleId, record.periodKey), record])
  )

  const groups = new Map<string, Group>()

  for (const registration of input.registrations) {
    const rule = getRuleForActivity(activeRules, registration.activityId)
    if (!rule) continue
    const key = groupKey(rule.id, registration.periodKey)
    const existing = groups.get(key)
    if (existing) {
      existing.activityIds.push(registration.activityId)
    } else {
      groups.set(key, { ruleId: rule.id, periodKey: registration.periodKey, activityIds: [registration.activityId] })
    }
  }

  // Include (rule, period) pairs that were granted before but now have no qualifying registrations
  // (e.g. everything cancelled) so their entitlement can be recomputed to 0 and revoked — but only
  // for rules that are still active.
  for (const record of input.alreadyGranted ?? []) {
    const key = groupKey(record.ruleId, record.periodKey)
    if (!groups.has(key) && activeRuleById.has(record.ruleId)) {
      groups.set(key, { ruleId: record.ruleId, periodKey: record.periodKey, activityIds: [] })
    }
  }

  const grants: FreeDropinGrant[] = []
  for (const group of groups.values()) {
    const rule = activeRuleById.get(group.ruleId)
    if (!rule) continue
    const qualifyingCount = qualifyingCountForRule(rule, group.activityIds)
    const entitledTickets = entitledTicketsForRule(rule, qualifyingCount)
    const alreadyGranted = alreadyGrantedByKey.get(groupKey(group.ruleId, group.periodKey))?.ticketsGranted ?? 0
    grants.push({
      ruleId: rule.id,
      packageId: rule.packageId,
      periodKey: group.periodKey,
      qualifyingCount,
      entitledTickets,
      alreadyGranted,
      delta: entitledTickets - alreadyGranted
    })
  }

  return grants
}

const activityIdsOfRule = (rule: FreeDropinTicketRule): string[] => rule.activityIds

const isPositiveInt = (n: number): boolean => Number.isInteger(n) && n > 0

function validateThresholds(rule: BulkFreeDropinRule, errors: FreeDropinRuleValidationError[]): void {
  const seenCounts = new Set<number>()
  for (const tier of rule.thresholds) {
    if (!isPositiveInt(tier.qualifyingCount) || !isPositiveInt(tier.ticketsGranted)) {
      errors.push({
        ruleId: rule.id,
        code: FreeDropinRuleValidationErrorCode.InvalidThreshold,
        message: `Rule "${rule.id}" has a threshold with non-positive/non-integer values.`
      })
    }
    if (seenCounts.has(tier.qualifyingCount)) {
      errors.push({
        ruleId: rule.id,
        code: FreeDropinRuleValidationErrorCode.DuplicateThresholdCount,
        message: `Rule "${rule.id}" has more than one threshold for qualifyingCount ${tier.qualifyingCount}.`
      })
    }
    seenCounts.add(tier.qualifyingCount)
  }

  // Rewards must not decrease as the qualifying count grows (a higher count granting fewer tickets
  // is almost certainly a misconfiguration).
  const ascending = [...rule.thresholds].sort((a, b) => a.qualifyingCount - b.qualifyingCount)
  for (let i = 1; i < ascending.length; i++) {
    if (ascending[i].ticketsGranted < ascending[i - 1].ticketsGranted) {
      errors.push({
        ruleId: rule.id,
        code: FreeDropinRuleValidationErrorCode.InvalidThreshold,
        message: `Rule "${rule.id}" thresholds grant fewer tickets at a higher qualifying count.`
      })
      break
    }
  }
}

/**
 * Static validation of a school's rule set against its drop-in packages. Returns all errors found
 * (empty array = valid). Checks: each activity belongs to at most one rule; every `packageId`
 * resolves to a package; non-empty activity sets; valid single/bulk ticket amounts and thresholds.
 */
export function validateFreeDropinRules(
  rules: FreeDropinTicketRule[],
  dropinPackages: DropinPackage[] = []
): FreeDropinRuleValidationError[] {
  const errors: FreeDropinRuleValidationError[] = []
  const packagesById = new Map(dropinPackages.map((pkg) => [pkg.id, pkg]))

  // Activity ownership: an activity may appear in at most one rule.
  const ruleIdsByActivity = new Map<string, Set<string>>()
  for (const rule of rules) {
    for (const activityId of activityIdsOfRule(rule)) {
      if (!activityId) continue
      const owners = ruleIdsByActivity.get(activityId) ?? new Set<string>()
      owners.add(rule.id)
      ruleIdsByActivity.set(activityId, owners)
    }
  }
  for (const [activityId, owners] of ruleIdsByActivity) {
    if (owners.size > 1) {
      errors.push({
        activityId,
        code: FreeDropinRuleValidationErrorCode.ActivityInMultipleRules,
        message: `Activity "${activityId}" is used by multiple rules: ${[...owners].join(', ')}.`
      })
    }
  }

  const seenRuleRevisions = new Set<string>()
  for (const rule of rules) {
    const dropinPackage = packagesById.get(rule.packageId)
    if (!dropinPackage) {
      errors.push({
        ruleId: rule.id,
        code: FreeDropinRuleValidationErrorCode.UnknownPackage,
        message: `Rule "${rule.id}" references unknown drop-in package "${rule.packageId}".`
      })
    } else {
      if (dropinPackage.isActive === false) {
        errors.push({
          ruleId: rule.id,
          code: FreeDropinRuleValidationErrorCode.InactivePackage,
          message: `Rule "${rule.id}" references inactive drop-in package "${rule.packageId}".`
        })
      }
      if (dropinPackage.numberOfClips !== 1) {
        errors.push({
          ruleId: rule.id,
          code: FreeDropinRuleValidationErrorCode.MultiClipPackage,
          message: `Rule "${rule.id}" references a package that does not contain exactly one clip.`
        })
      }
    }

    if (activityIdsOfRule(rule).filter(Boolean).length === 0) {
      errors.push({
        ruleId: rule.id,
        code: FreeDropinRuleValidationErrorCode.EmptyActivitySet,
        message: `Rule "${rule.id}" has no qualifying activities.`
      })
    }

    const hasRevision = rule.revision !== undefined
    const hasEffectiveFrom = rule.effectiveFrom !== undefined
    const hasEffectiveUntil = rule.effectiveUntil !== undefined
    const revisionIsValid = rule.revision !== undefined && isPositiveInt(rule.revision)
    const effectiveFromIsValid =
      rule.effectiveFrom !== undefined && rule.effectiveFrom.length > 0 && !Number.isNaN(Date.parse(rule.effectiveFrom))
    const effectiveUntilIsValid =
      rule.effectiveUntil === undefined ||
      (rule.effectiveUntil.length > 0 &&
        !Number.isNaN(Date.parse(rule.effectiveUntil)) &&
        effectiveFromIsValid &&
        Date.parse(rule.effectiveUntil) > Date.parse(rule.effectiveFrom as string))
    if (
      hasRevision !== hasEffectiveFrom ||
      (!hasRevision && hasEffectiveUntil) ||
      (hasRevision && (!revisionIsValid || !effectiveFromIsValid || !effectiveUntilIsValid))
    ) {
      errors.push({
        ruleId: rule.id,
        code: FreeDropinRuleValidationErrorCode.InvalidRuleRevision,
        message: `Rule "${rule.id}" has malformed revision metadata.`
      })
    }

    const revisionKey = `${rule.id}::${rule.revision ?? 'legacy'}`
    if (seenRuleRevisions.has(revisionKey)) {
      errors.push({
        ruleId: rule.id,
        code: FreeDropinRuleValidationErrorCode.DuplicateRuleRevision,
        message: `Rule "${rule.id}" contains duplicate revision "${rule.revision ?? 'legacy'}".`
      })
    }
    seenRuleRevisions.add(revisionKey)

    if (rule.ruleType === FreeDropinRuleType.SingleActivity) {
      if (!isPositiveInt(rule.ticketsGranted)) {
        errors.push({
          ruleId: rule.id,
          code: FreeDropinRuleValidationErrorCode.InvalidThreshold,
          message: `Rule "${rule.id}" grants a non-positive/non-integer number of tickets.`
        })
      }
    } else {
      if (rule.thresholds.length === 0) {
        errors.push({
          ruleId: rule.id,
          code: FreeDropinRuleValidationErrorCode.InvalidThreshold,
          message: `Bulk rule "${rule.id}" has no thresholds.`
        })
      }
      validateThresholds(rule, errors)
    }
  }

  return errors
}
